import React, { useState, useEffect } from 'react';
import { COLORS, sharedStyles, callClaude } from '../shared';

// CFF's 86-letter library — categories and templates
const LETTER_LIBRARY = {
  'dispute': {
    label: 'Standard Dispute Letter',
    template: (data) => `
[CLIENT NAME]
[ADDRESS]
[CITY, STATE ZIP]
[DATE]

[BUREAU NAME]
Consumer Disputes
[BUREAU ADDRESS]

Re: Formal Dispute of Inaccurate Credit Information
Account: ${data.creditor} — Account #${data.accountNumber || 'XXXX'}

To Whom It May Concern:

I am writing pursuant to my rights under the Fair Credit Reporting Act (FCRA), 15 U.S.C. § 1681 et seq., to dispute the following inaccurate information appearing on my credit report:

Creditor: ${data.creditor}
Account Number: ${data.accountNumber || 'On File'}
Bureau Reporting: ${data.bureau}
Issue: ${data.issue}

This information is inaccurate because: ${data.fcraGrounds || 'the information reported does not accurately reflect my credit history.'}

Pursuant to 15 U.S.C. § 1681i, I request that you investigate this matter and correct or delete the inaccurate information within 30 days of receipt of this letter.

Please provide me with:
1. Written notification of the results of your investigation
2. A copy of my updated credit report reflecting any corrections
3. The name and contact information of any furnisher contacted during your investigation

I have enclosed copies of relevant documentation to support my dispute. Please do not share my information with any third parties not necessary to complete this investigation.

Sincerely,

[CLIENT SIGNATURE]
[CLIENT NAME]
[SSN Last 4: XXXX]

Enclosures: Copy of ID, Supporting Documents
`.trim()
  },
  'goodwill': {
    label: 'Goodwill Deletion Letter',
    template: (data) => `
[CLIENT NAME]
[ADDRESS]
[CITY, STATE ZIP]
[DATE]

${data.creditor}
Customer Relations Department
[CREDITOR ADDRESS]

Re: Goodwill Adjustment Request — Account #${data.accountNumber || 'XXXX'}

Dear ${data.creditor} Customer Relations Team:

I am writing to request a goodwill deletion of a negative item on my credit report. I have been a customer of ${data.creditor} and value our relationship.

The account in question:
Creditor: ${data.creditor}
Account Number: ${data.accountNumber || 'On File'}
Issue: ${data.issue}

I sincerely acknowledge that the late payment(s) occurred and take full responsibility. This was due to circumstances that have since been resolved. My payment history before and after this incident demonstrates my commitment to honoring my financial obligations.

I am requesting that you consider removing this negative mark as a gesture of goodwill. This one item is significantly impacting my credit score and my ability to ${data.goodwillReason || 'achieve my financial goals'}.

I understand that you are not legally required to grant this request, but I respectfully ask that you consider my overall account history and my commitment to your company.

Thank you sincerely for your time and consideration.

Respectfully,

[CLIENT SIGNATURE]
[CLIENT NAME]
Account: ${data.accountNumber || 'On File'}
`.trim()
  },
  'debt-validation': {
    label: 'Debt Validation Letter',
    template: (data) => `
[CLIENT NAME]
[ADDRESS]
[CITY, STATE ZIP]
[DATE]

${data.creditor || 'Collection Agency'}
[COLLECTOR ADDRESS]

Re: Debt Validation Request — Account #${data.accountNumber || 'XXXX'}
NOTICE: This is NOT a refusal to pay, but a notice that your claim is disputed.

To Whom It May Concern:

Pursuant to the Fair Debt Collection Practices Act (FDCPA), 15 U.S.C. § 1692g, I am formally requesting validation of the alleged debt you claim I owe.

This notice is sent within 30 days of your initial communication. You are hereby notified that:

1. I dispute the validity of this alleged debt in its entirety
2. I demand that you cease all collection activity until this debt is properly validated

Please provide the following:
• Proof that your company is licensed to collect debts in my state
• The original signed contract creating the alleged debt
• Complete account history and payment records
• Proof of your legal right to collect this debt
• The original creditor's name and address
• Confirmation this debt is within the statute of limitations

If you cannot validate this debt, you are required to cease all collection efforts and request deletion from all credit bureaus.

This letter is being sent via Certified Mail. Do not contact me by phone.

Sincerely,

[CLIENT SIGNATURE]
[CLIENT NAME]

Sent via: USPS Certified Mail — Return Receipt Requested
`.trim()
  },
  'pay-for-delete': {
    label: 'Pay-for-Delete Letter',
    template: (data) => `
[CLIENT NAME]
[ADDRESS]
[CITY, STATE ZIP]
[DATE]

${data.creditor || 'Collection Agency'}
[COLLECTOR ADDRESS]

Re: Settlement and Deletion Offer — Account #${data.accountNumber || 'XXXX'}
CONFIDENTIAL SETTLEMENT OFFER

To Whom It May Concern:

I am writing to propose a settlement agreement regarding the above-referenced account. I am prepared to resolve this matter in full in exchange for the complete deletion of this account from all three major credit bureaus (Equifax, Experian, and TransUnion).

My Settlement Offer:
• Account Balance Reported: ${data.balance || '[BALANCE]'}
• Settlement Amount Offered: [SETTLEMENT AMOUNT]
• Condition: Complete deletion from all credit bureau records within 30 days of payment

This offer is contingent upon your written agreement to:
1. Accept the settlement amount as payment in full
2. Immediately request deletion (not just a "paid" update) from all three bureaus
3. Provide written confirmation of this agreement before payment is made

Please respond in writing within 15 days. This offer is time-sensitive and will expire if not accepted by [EXPIRATION DATE].

Note: Payment will be made only after I receive your written agreement to delete. I will not make any payment without this written confirmation.

Sincerely,

[CLIENT SIGNATURE]
[CLIENT NAME]
Phone: [PHONE NUMBER]
`.trim()
  },
  'inquiry-removal': {
    label: 'Inquiry Removal Letter',
    template: (data) => `
[CLIENT NAME]
[ADDRESS]
[CITY, STATE ZIP]
[DATE]

${data.bureau} Credit Bureau
Consumer Disputes Department
[BUREAU ADDRESS]

Re: Unauthorized Hard Inquiry Removal Request
Creditor: ${data.creditor}
Inquiry Date: ${data.date || '[DATE OF INQUIRY]'}

To Whom It May Concern:

I am writing to dispute an unauthorized hard inquiry that appears on my credit report. Under the FCRA, a hard inquiry may only be placed on my credit report with my express written permission.

Inquiry Details:
Creditor/Company: ${data.creditor}
Date of Inquiry: ${data.date || '[DATE]'}
Bureau: ${data.bureau}

I did not authorize this company to access my credit report. This inquiry was made without my knowledge or consent and constitutes a violation of my rights under 15 U.S.C. § 1681b(a).

I request that you:
1. Investigate this unauthorized inquiry immediately
2. Remove this inquiry from my credit report within 30 days
3. Provide me with written confirmation of the deletion

If you have documentation showing I authorized this inquiry, please provide it. Otherwise, federal law requires its removal.

Sincerely,

[CLIENT SIGNATURE]
[CLIENT NAME]
SSN Last 4: XXXX
`.trim()
  },
};

export default function DisputeLetters({ report, strategy, selectedActions }) {
  const [letters, setLetters]     = useState({});
  const [loading, setLoading]     = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [copied, setCopied]       = useState({});
  const [error, setError]         = useState({});

  useEffect(() => {
    if (selectedActions?.length > 0 && activeTab === null) {
      setActiveTab(selectedActions[0].id);
    }
  }, [selectedActions]);

  const matchLibrary = (action) => {
    const type = action.letterType?.toLowerCase();
    return LETTER_LIBRARY[type] || LETTER_LIBRARY['dispute'];
  };

  const generateLetter = async (action) => {
    // First — try library match
    const libraryMatch = matchLibrary(action);
    const libraryDraft = libraryMatch.template({
      creditor:      action.creditor,
      accountNumber: 'XXXX',
      bureau:        action.bureau,
      issue:         action.issue,
      balance:       report?.accounts?.find(a => a.creditor === action.creditor)?.balance,
      date:          report?.inquiries?.find(i => i.creditor === action.creditor)?.date,
      fcraGrounds:   action.fcraGrounds,
    });

    setLetters(prev => ({ ...prev, [action.id]: { draft: libraryDraft, enhanced: null, source: 'library', libraryType: libraryMatch.label } }));
    setLoading(prev => ({ ...prev, [action.id]: 'enhancing' }));

    // Then — enhance with AI for this client's specific details
    try {
      const system = `You are a credit dispute letter specialist at Coleman Financial Firm. You write FCRA-compliant, professional dispute letters. Return only the letter text — no explanations, no JSON.`;
      const prompt = `Take this base letter template and enhance it with the specific details from this client's credit report. Make it more persuasive and personalized, but keep it professional and FCRA-compliant. Do not add placeholder brackets — fill in what you know or leave generic professional language.

Base Template:
${libraryDraft}

Client Report Data:
- Client: ${report?.clientName || 'Client'}
- Issue: ${action.issue}
- Action Needed: ${action.action}
- FCRA Grounds: ${action.fcraGrounds}
- Letter Type: ${action.letterType}
- Bureau: ${action.bureau}
- Creditor: ${action.creditor}

Return ONLY the enhanced letter text, ready to send (with [FILL IN] placeholders for personal info like address, signature, date).`;

      const enhanced = await callClaude([{ role:'user', content:prompt }], system);
      setLetters(prev => ({ ...prev, [action.id]: { ...prev[action.id], enhanced } }));
    } catch(err) {
      setError(prev => ({ ...prev, [action.id]: 'Enhancement failed — using library template.' }));
    } finally {
      setLoading(prev => ({ ...prev, [action.id]: null }));
    }
  };

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [id]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
  };

  const s = {
    layout:     { display:'grid', gridTemplateColumns:'240px 1fr', gap:20 },
    tabList:    { display:'flex', flexDirection:'column', gap:8 },
   tab: (a) => ({ padding:'12px 14px', background: a?'rgba(46,125,50,0.08)':'#ffffff', border:`1px solid ${a?COLORS.green:COLORS.border}`, borderRadius:8, cursor:'pointer', transition:'all 0.15s' }),
    tabName:    { color: COLORS.text, fontSize:'12px', fontWeight:600, marginBottom:3 },
    tabType:    { color: COLORS.muted, fontSize:'10px' },
    tabStatus:  { display:'flex', alignItems:'center', gap:4, marginTop:4 },
    letterBox:  { background:'#f8f9fb', border:`1px solid ${COLORS.border}`, borderRadius:8, padding:20, minHeight:400, fontFamily:"'Courier New', monospace", fontSize:'12px', color: COLORS.text, lineHeight:1.8, whiteSpace:'pre-wrap', overflowY:'auto', maxHeight:'500px' },
    actionRow:  { display:'flex', gap:10, marginTop:12 },
    copyBtn:    { flex:1, padding:'10px', background:'#f0f4fa', border:`1px solid ${COLORS.gold}`, borderRadius:7, color: COLORS.gold, fontSize:'12px', fontWeight:600, cursor:'pointer' },
    genBtn:     { flex:1, padding:'10px', background: COLORS.gold, border:`1px solid ${COLORS.gold}`, borderRadius:7, color:'#fff', fontSize:'12px', fontWeight:600, cursor:'pointer' },
    sourceTag:  (src) => ({ display:'inline-block', padding:'2px 8px', borderRadius:3, fontSize:'10px', fontWeight:700, background: src==='library'?'rgba(201,168,76,0.15)':'rgba(76,175,125,0.15)', color: src==='library'?COLORS.gold:COLORS.green, marginBottom:10 }),
    mailingBox: { background:'rgba(201,168,76,0.06)', border:`1px solid rgba(201,168,76,0.2)`, borderRadius:8, padding:14, marginTop:16 },
    mailingTitle: { color: COLORS.gold, fontWeight:700, fontSize:'12px', marginBottom:10 },
    costRow:    { display:'flex', justifyContent:'space-between', color: COLORS.muted, fontSize:'12px', padding:'4px 0', borderBottom:`1px solid ${COLORS.border}` },
    totalRow:   { display:'flex', justifyContent:'space-between', color: COLORS.gold, fontWeight:700, fontSize:'13px', paddingTop:8, marginTop:4 },
    priorityBadge: (p) => ({ fontSize:'10px', fontWeight:700, padding:'2px 6px', borderRadius:3, color: p==='HIGH'?'#c0392b':p==='MEDIUM'?COLORS.gold:COLORS.green, background: p==='HIGH'?'rgba(192,57,43,0.15)':p==='MEDIUM'?'rgba(201,168,76,0.12)':'rgba(76,175,125,0.12)' }),
  };

  const currentAction = selectedActions?.find(a => a.id === activeTab);
  const currentLetter = letters[activeTab];
  const currentLoading = loading[activeTab];

  const displayText = currentLetter?.enhanced || currentLetter?.draft || '';

  const MAILING_COSTS = {
    postage: 0.73,
    certified: 4.40,
    returnReceipt: 2.10,
    printing: 0.50,
  };
  const totalPerLetter = Object.values(MAILING_COSTS).reduce((a,b) => a+b, 0);

  return (
    <div style={sharedStyles.main}>
      <div style={sharedStyles.topBar}>
        <div>
          <div style={sharedStyles.pageTitle}>DISPUTE <span style={sharedStyles.pageTitleAccent}>LETTERS</span></div>
          <div style={sharedStyles.pageSubtitle}>
            {selectedActions?.length} letter{selectedActions?.length!==1?'s':''} ready to generate — matched from CFF's dispute library and personalized for your client.
          </div>
        </div>
        <div style={sharedStyles.memberBadge}>⭐ CFF MEMBER</div>
      </div>

      <div style={s.layout}>
        {/* LEFT — LETTER TABS */}
        <div>
          <div style={{ color: COLORS.gold, fontSize:'10px', letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, marginBottom:10 }}>Letters to Generate</div>
          <div style={s.tabList}>
            {selectedActions?.map(action => {
              const lib     = matchLibrary(action);
              const ltr     = letters[action.id];
              const isLoading = loading[action.id];
              return (
                <div key={action.id} style={s.tab(activeTab===action.id)} onClick={() => setActiveTab(action.id)}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div style={s.tabName}>{action.creditor}</div>
                    <span style={s.priorityBadge(action.priority)}>{action.priority}</span>
                  </div>
                  <div style={s.tabType}>{lib.label}</div>
                  <div style={s.tabStatus}>
                    {!ltr && !isLoading && <span style={{ color:'#555', fontSize:'10px' }}>Not generated</span>}
                    {isLoading==='enhancing' && <span style={{ color: COLORS.gold, fontSize:'10px' }}>✨ Enhancing...</span>}
                    {ltr && !isLoading && <span style={{ color: COLORS.green, fontSize:'10px' }}>✓ Ready</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CLICK2MAIL AFFILIATE LINK */}
          <div style={{ marginTop:16, padding:14, background:'#f0f4fa', border:`1px solid ${COLORS.border}`, borderRadius:8 }}>
            <div style={{ color: COLORS.gold, fontWeight:700, fontSize:'12px', marginBottom:6 }}>📬 Mail Your Letters</div>
<div style={{ color: COLORS.muted, fontSize:'11px', marginBottom:10, lineHeight:1.5 }}>Send via USPS Certified Mail with Return Receipt — recommended for all bureau disputes. <strong>Note:</strong> Click2Mail may take 20-30 seconds to load.</div>
<a rel="nofollow" href="https://click2mail.com/?affiliate_code=HPS2Cj6aSc&referring_service=banner&element_id=1" target="_blank">
              <img src="https://click2mail.com/media/amasty_affiliate/banner/NPF_Banner.jpeg" alt="Send via Click2Mail" style={{ width:'100%', borderRadius:6, display:'block' }} />
            </a>
          </div>
        </div>

        {/* RIGHT — LETTER DISPLAY */}
        <div>
          {!currentAction ? (
            <div style={{ ...sharedStyles.card, textAlign:'center', padding:'60px 20px' }}>
              <div style={{ fontSize:'40px', marginBottom:12 }}>✉️</div>
              <div style={{ color: COLORS.muted, fontSize:'14px' }}>Select a letter from the left to preview or generate it.</div>
            </div>
          ) : (
            <div style={sharedStyles.card}>
              <div style={sharedStyles.cardHeader}>
                <div style={sharedStyles.stepBadge}>✉️</div>
                <div>
                  <div style={sharedStyles.cardTitle}>{currentAction.creditor} — {matchLibrary(currentAction).label}</div>
                  <div style={{ color: COLORS.muted, fontSize:'11px', marginTop:2 }}>{currentAction.bureau} • {currentAction.issue}</div>
                </div>
              </div>

              {currentLetter && (
                <div style={s.sourceTag(currentLetter.enhanced ? 'enhanced' : 'library')}>
                  {currentLetter.enhanced ? '✨ Enhanced with client details' : `📚 From CFF Library: ${currentLetter.libraryType}`}
                </div>
              )}

              {error[activeTab] && (
                <div style={{ color: COLORS.gold, fontSize:'11px', marginBottom:8 }}>⚠️ {error[activeTab]}</div>
              )}

              {currentLoading === 'enhancing' && (
                <div style={{ color: COLORS.gold, fontSize:'12px', marginBottom:12, padding:'8px 12px', background:'rgba(201,168,76,0.08)', borderRadius:6 }}>
                  ✨ Personalizing letter with client details...
                </div>
              )}

              {displayText ? (
                <div style={s.letterBox}>{displayText}</div>
              ) : (
                <div style={{ ...s.letterBox, display:'flex', alignItems:'center', justifyContent:'center', color:'#444' }}>
                  Click "Generate Letter" to create this letter
                </div>
              )}

              <div style={s.actionRow}>
                <button
                  style={s.genBtn}
                  onClick={() => generateLetter(currentAction)}
                  disabled={!!currentLoading}
                >
                  {currentLoading ? '⏳ Working...' : currentLetter ? '🔄 Regenerate' : '✨ Generate Letter'}
                </button>
                {displayText && (
                  <button style={s.copyBtn} onClick={() => handleCopy(activeTab, displayText)}>
                    {copied[activeTab] ? '✓ Copied!' : '📋 Copy Letter'}
                  </button>
                )}
              </div>

              <div style={{ color: COLORS.muted, fontSize:'11px', marginTop:12, padding:'10px', background:'#f0f4fa', borderRadius:6, lineHeight:1.6 }}>
                ⚠️ Fill in all [BRACKET] fields before sending. Coleman Financial Firm recommends sending via USPS Certified Mail with Return Receipt for all bureau disputes.
              </div>
            </div>
          )}

          {/* GENERATE ALL */}
          {selectedActions?.length > 1 && (
            <button
              style={{ width:'100%', marginTop:12, padding:'12px', background:'#1B3A6B', border:`1px solid #1B3A6B`, borderRadius:8, color:'#fff', fontSize:'13px', fontWeight:600, cursor:'pointer', letterSpacing:'0.06em' }}
              onClick={() => selectedActions.forEach(a => { if (!letters[a.id]) generateLetter(a); })}
            >
              📚 Generate All {selectedActions.length} Letters at Once
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
